import React from 'react';
import { Box, FormControl, Select, MenuItem, Typography } from '@mui/material';
import { GridFooter as GridFooterCommunity } from '@mui/x-data-grid';
import { GridFooter as GridFooterPro } from '@mui/x-data-grid-pro';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import { useState, useEffect, useRef } from 'react';
import { useHistory } from 'react-router';

const AutoRefreshGridFooter = React.forwardRef((props, ref) => {
    const { autorefresh, intervals: intervalsProp, deps, onTick, gridFlavor = 'pro', ...gridFooterProps } = props;
    const GridFooter = gridFlavor === 'community' ? GridFooterCommunity : GridFooterPro;
    const history = useHistory();
    const searchParams = new URLSearchParams(history.location.search);
    const urlAuto = searchParams.get('autorefresh');
    const enabled = autorefresh !== false && urlAuto !== 'false';
    const [autoRefreshInterval, setAutoRefreshInterval] = React.useState(0);
    const [lastRefreshTime, setLastRefreshTime] = React.useState(new Date());

    // Default intervals if none provided
    const defaultIntervals = [
        { interval: 0, label: "Auto-refresh off" },
        { interval: 5, label: "Refresh every 5 seconds" },
        { interval: 10, label: "Refresh every 10 seconds" },
        { interval: 30, label: "Refresh every 30 seconds" },
        { interval: 60, label: "Refresh every 1 minute" },
        { interval: 300, label: "Refresh every 5 minutes" },
        { interval: 600, label: "Refresh every 10 minutes" }
    ];

    const intervals = intervalsProp || defaultIntervals;

    // hook to trigger refresh events when deps change
    useRefreshTrigger(deps);

    React.useEffect(() => {
        if (autoRefreshInterval === 0) return;
        
        const timer = setInterval(() => {
            if (deps) {
                deps.current = Date.now();
            }
            onTick?.();
            setLastRefreshTime(new Date());
        }, autoRefreshInterval * 1000);

        return () => clearInterval(timer);
    }, [autoRefreshInterval, deps, onTick]);

    return (
        <>
            <Box ref={ref} sx={{ 
                borderTop: '1px solid rgba(224, 224, 224, 1)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: '0.5rem'
            }}>
                {enabled && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <FormControl size="small" sx={{ minWidth: 200 }}>
                            <Select
                                value={autoRefreshInterval}
                                onChange={(e) => setAutoRefreshInterval(e.target.value)}
                                displayEmpty
                                variant="outlined"
                                startAdornment={<AutorenewIcon sx={{ mr: 1 }} />}
                            >
                                {intervals.map((item) => (
                                    <MenuItem key={item.interval} value={item.interval}>
                                        {item.label}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        {lastRefreshTime && autoRefreshInterval > 0 && (
                            <Typography variant="caption" color="textSecondary">
                                Last updated: {lastRefreshTime.toLocaleTimeString()}
                            </Typography>
                        )}
                    </Box>
                )}
                <div style={{ flexGrow: 1 }}>
                    <GridFooter 
                        {...gridFooterProps} 
                        sx={{ borderTop: 'none' }}
                    />
                </div>
            </Box>
        </>
    );
});

export default AutoRefreshGridFooter;

export const useRefreshTrigger = (internalRefreshTrigger, inFlightRef) => {
    const [refreshTriggerValue, setRefreshTriggerValue] = useState(Date.now());

    useEffect(() => {
        const interval = setInterval(() => {
            // A tick that lands while the previous fetch is still running is
            // deferred, not dropped: the ref keeps the newer stamp, so the poll
            // picks it up on the first tick after that fetch settles. Firing on
            // top of an in-flight request only cancels the BROWSER side of it --
            // the AbortController does nothing to the Django request or the
            // ClickHouse/Postgres query behind it, which run to completion -- so
            // "refresh every 5 seconds" on a 20-second report meant an unbounded
            // pile of concurrent server queries (CPBACKEND-BF36, 2026-09-17).
            if (inFlightRef?.current) {
                return;
            }
            if (internalRefreshTrigger.current !== refreshTriggerValue) {
                setRefreshTriggerValue(internalRefreshTrigger.current);
            }
        }, 100);
        return () => clearInterval(interval);
    }, [refreshTriggerValue, internalRefreshTrigger, inFlightRef]);

    return refreshTriggerValue;
};

/**
 * @param {React.MutableRefObject<boolean>} [inFlightRef] set true by the grid while
 *   its list fetch is running; auto-refresh ticks are held until it clears.
 */
export const useAutoRefresh = (inFlightRef) => {
    const refreshTrigger = useRef(Date.now());
    const refreshTriggerValue = useRefreshTrigger(refreshTrigger, inFlightRef);

    return {
        refreshTrigger,
        refreshTriggerValue
    };
}; 